var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment, Suspense, useCallback, useEffect, useRef, useState, } from 'react';
import { connect } from 'redux-zero/react';
import { EventType } from '@assets/utils/customEvent';
import ErrorBoundary from '@embed/components/common/ErrorBoundary';
import QuantcastTag from '@embed/components/common/QuantcastTag';
import { WidgetWrapper } from '@embed/components/Player3/components/WidgetWrapper';
import { PlayerLayout } from '@embed/components/Player3/store/types';
import { VideoPlayerOpenReason } from '@embed/components/VideoPlayer/types';
import { useEmbedAppContext, } from '@embed/hooks/useEmbedAppContext';
import { useEmbedFeed } from '@embed/hooks/useEmbedFeed';
const VideoPlayer = React.lazy(() => import('@embed/components/VideoPlayer'));
const mapStateToProps = ({ params }) => ({ params });
const PureProgrammaticPlayer = (props) => {
    const { appContext, params, actions, muted, pip } = props;
    const { publisherClient, channel } = appContext;
    useEffect(() => {
        if (publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.config) {
            actions.fetchedParams({
                fetchedParams: publisherClient.config,
            });
            actions.fetchExperiments({ publisherClient });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publisherClient]);
    useEffect(() => {
        if (channel === null || channel === void 0 ? void 0 : channel.config) {
            actions.fetchedParams({
                fetchedParams: channel.config,
            });
        }
    }, [channel, actions]);
    const programmaticPlayerRef = useRef();
    useEffect(() => {
        actions.setContext({ appContext });
    }, [appContext]);
    useEffect(() => {
        actions.trackEmbedInstance({ appContext });
        actions.trackEmbedImpression({
            container: programmaticPlayerRef.current,
            eventType: EventType.FW_PROGRAMMATICPLAYER_IMPRESSION,
        });
    }, [appContext.embedInstanceId]);
    const [{ videos, hasMore }, feedActions] = useEmbedFeed({
        config: appContext.embedFeedConfig,
        params,
        guestId: appContext.guestId,
        performance: appContext.performance,
    });
    const [video, setVideo] = useState(videos[0]);
    useEffect(() => {
        setVideo(videos[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videos[0]]);
    const onNavigateToVideo = useCallback(({ video, navigationType, countOfNextVideos }) => {
        actions.setPlayTrigger({
            video,
            type: navigationType,
        });
        setVideo(video);
        if (countOfNextVideos <= 3 && hasMore) {
            feedActions.loadMore();
        }
    }, [actions, feedActions, hasMore]);
    const [isVideoPlayerOpened, setIsVideoPlayerOpened] = useState(true);
    const onVideoPlayerClose = useCallback(() => {
        setIsVideoPlayerOpened(false);
    }, []);
    return isVideoPlayerOpened ? (<>
      <Suspense fallback={<Fragment />}>
        <VideoPlayer actions={actions} appContext={appContext} playerLayout={params.minimized ? PlayerLayout.MINIMIZED : PlayerLayout.FULLSCREEN} playerOpenReason={VideoPlayerOpenReason.PROGRAMMATIC} muted={muted} onClose={onVideoPlayerClose} params={params} pip={pip} onNavigateToVideo={onNavigateToVideo} triggerFetchNextVideos={feedActions.loadMore} video={video} videos={videos}/>
      </Suspense>
      <ErrorBoundary discardOnError={true}>
        <QuantcastTag />
      </ErrorBoundary>
    </>) : null;
};
export const ConnectedProgrammaticPlayer = connect(mapStateToProps)((props) => {
    const { params, actions, appContextType } = props;
    const appContext = useEmbedAppContext({ params, actions, appContextType });
    if (!appContext) {
        return null;
    }
    return <PureProgrammaticPlayer {...props} appContext={appContext}/>;
});
export const ProgrammaticPlayer = (_a) => {
    var { initialParams, widgetParams, appContextType } = _a, rest = __rest(_a, ["initialParams", "widgetParams", "appContextType"]);
    return (<WidgetWrapper initialParams={initialParams} widgetParams={widgetParams}>
      {(actions) => (<ConnectedProgrammaticPlayer actions={actions} appContextType={appContextType} {...rest}/>)}
    </WidgetWrapper>);
};
